import { formatInTimeZone } from 'date-fns-tz'

import { createInstance } from '@/helpers/api/base'
import { FilterType, ICreateMileageLossRequest, Log } from '@/types/logs'
import { useNotification } from '@/ui/Notification'
import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { useGetUserInfo } from '@/modules/Auth'
import { useWorkstationStore } from '@/modules/Workstation/store'
import { useCombinedLogsTableFormStateStore, useCombinedLogsTableStateStore } from '../store'
import { useGetReference } from './useGetReference'
import { useGetControllerLogParams } from '../apiHelpers/useGetControllerLogParams'
import { formatMileageLossRequestBody } from '../helpers/formatMileageLossRequestBody'

export const useCreateMileageLoss = (log: Log) => {
    const queryClient = useQueryClient()
    const { showNotification } = useNotification()
    const { data: userInfo } = useGetUserInfo()
    const { workstation } = useWorkstationStore()
    const { data: reference } = useGetReference()
    const { setFilter } = useCombinedLogsTableStateStore()
    const { setSelectedRowId, setRoute } = useCombinedLogsTableFormStateStore()
    const logParams = useGetControllerLogParams()

    const createMileageLossMutation = useAppMutation<Log, Error, ICreateMileageLossRequest, Log[]>({
        mutationFn: (data) =>
            createInstance({
                url: '/controller-logs',
                method: 'POST',
                data,
            }),
        alertOnError: true,
        queryKey: ['controllerLogs', ...logParams],
        optimisticChangesCallback: (requestBody: ICreateMileageLossRequest, previousLogs?: Log[]) => [
            ...(previousLogs || []),
            formatMileageLossRequestBody(requestBody),
        ],
        onSuccess: async (_, response) => {
            await queryClient.invalidateQueries({ queryKey: ['reference'] })
            showNotification({ message: 'Mileage created' })

            setFilter(FilterType.All)
            setSelectedRowId(response.id)
            setRoute(response.routes[0])
        },
    })

    const handleCreateMileageLoss = () => {
        createMileageLossMutation.mutate({
            id: crypto.randomUUID(),
            reference: reference,
            routes: log.routes,
            routeSelectionMode: log.routeSelectionMode,
            trip: log.trip,
            rtnTrips: log.rtnTrips,
            run: log.run,
            duty: log.duty,
            fleet: log.fleet,
            author: userInfo?.lastSessionDetails.authorName,
            timeStamp: formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'"),
            workstation: workstation?.workstationNumber,
            originalTrip: log.originalTrip,
            eventLogId: log.id,
        })
    }

    return {
        handleCreateMileageLoss,
    }
}
